import { useState } from "react";
import { useIntl } from "react-intl";
import VehicleChildForm from "./components/vehicleChildForm";
import { requiredFieldsByStep, getRequiredFields } from "./utils/required-fields-by-step";
import DriverChildForm from "./components/driverChildForm";
import TowTruckContactsChildForm from "./components/towTruckContactsChildForm";
import DealershipChildForm from "./components/dealershipChildForm";
import ServiceChildForm from "./components/serviceChildForm";
import ErrorDialog from "../../../../components/dialogs/ErrorDialog";
import SuccessDialog from "../../../../components/dialogs/SuccessDialog";
import { IForm } from "../../";
import { lastSaveConflict } from "../../../../utils/lastSaveConflict";
import { Occurrence } from "../../../../models/occurrence";
import { formatFieldLabel } from "./utils/fields-labels";
import {
  OccurrenceStepEnum,
  OccurrenceStepLabelIdMap,
} from "../../../../enums/occurrenceStep";
import { FinalizeOccurrenceButton } from "../../dialogs/finalizationReasonDialog";

const DisplacementForm = (props: IForm) => {
  const intl = useIntl();
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
  const [changeStepErrorMessage, setChangeStepErrorMessage] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  const requiredServiceFields = getRequiredFields(
    props.occurrence?.occurrenceType === "P" ? "DISPLACEMENT_PREVENTIVE_CHECKUP" : "DISPLACEMENT",
    props.occurrence?.occurrenceType
  );

  return (
    <div>
      <div className="text-size-h4 font-bold">{intl.formatMessage({ id: "intl-msg:kanban.form.displacementForm.title" })}</div>
      <div className="text-color-status-resting text-size-14">
        {intl.formatMessage({ id: "intl-msg:kanban.form.displacementForm.description" })}
      </div>

      <VehicleChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={["assetId", "protocolNumber"]}
        disabledFields={["vehicleChassis"]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
        scheduling={props.scheduling}
      />

      <DriverChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[]}
        disabledFields={[]}
        disabledAllFields={true}
        requiredFields={requiredFieldsByStep.DRIVER}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <TowTruckContactsChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[]}
        disabledFields={[]}
        requiredFields={[]}
        disabledAllFields={true}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <DealershipChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[
          "dealershipId",
          "dealershipUniqueId",
          "dealershipLocation",
          "dealershipRepresentative",
          "dealershipRegional",
          "dealershipPhone",
          "dealershipDemarcatedArea",
        ]}
        disabledFields={[]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <ServiceChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[
          "timeOpenProtocol",
          "country",
          "partOrderStatus",
          "literatureDiagnosis",
          "literatureTroubleshooting",
          "tasNumber",
          "tasStatus",
          "checklist",
          "solutionProposed",
          "vehicleEntryAt",
          "vehicleExitAt",
        ]}
        disabledFields={[
          "criticality",
          "createdBy",
          "modules",
          "estimateTimeRepair",
          "occurrenceType",
          "hasLink",
          "mainOccurrence",
          "source",
          "vehiclePayloadType",
          "vehicleMaximumPayload",
          "vehicleCriticalPayload",
          "serviceBaySchedule",
          "dynamicsOccurrenceCode",
        ]}
        disabledAllFields={props.disabledAllFields}
        requiredFields={requiredServiceFields}
        step={props.step}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        partners={props.partners}
        hasError={hasError}
        persona={props.persona}
        scheduling={props.scheduling}
      />

      <hr className="my-6" />

      {props.persona !== "MANAGER" &&
        !props.finalized &&
        !props.hiddenSaveButton && (
          <div className="flex justify-end gap-3">
            <button
              className={`btn btn-primary ${
                props.loading ? "btn-loading disabled" : ""
              }`}
              type="button"
              onClick={async () => {
                if (!props.occurrence) return;
                const isValid = props.isValidStep(requiredServiceFields, props.occurrence);
                setHasError(!isValid);
                if (!isValid) {
                  setShowValidationErrorDialog(true);
                  return;
                }
                const success = await props.save(props.occurrence);
                if (success) {
                  setShowSuccessDialog(true);
                } else if (!lastSaveConflict.current) {
                  setShowErrorDialog(true);
                }
              }}
              disabled={props.loading || props.disabledAllFields}
            >
              {intl.formatMessage({ id: "intl-msg:occurrence.save" })}
            </button>

            <FinalizeOccurrenceButton
              occurrence={props.occurrence}
              finishOccurrence={props.finishOccurrence}
              disabledAllFields={props.disabledAllFields}
              persona={props.persona}
              stepId={props.step?.id}
            />
          </div>
        )}

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.displacementForm.saveErrorTitle" })}
        body={intl.formatMessage({ id: "intl-msg:kanban.form.displacementForm.saveErrorBody" })}
      />

      <ErrorDialog
        show={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.displacementForm.missingPartTitle" })}
        body={intl.formatMessage({ id: "intl-msg:kanban.form.displacementForm.missingPartBody" })}
      />

      <ErrorDialog
        show={!!changeStepErrorMessage}
        onClose={() => setChangeStepErrorMessage(null)}
        title={intl.formatMessage({ id: "intl-msg:occurrence.step.errorChangeStepTitle" })}
        body={changeStepErrorMessage ?? ""}
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.displacementForm.successTitle" })}
        body={
          <div className="space-y-4">
            <div>{intl.formatMessage({ id: "intl-msg:kanban.form.displacementForm.successBody" })}</div>
            {!hasError && (
              <div>
                {intl.formatMessage({ id: "intl-msg:kanban.form.displacementForm.advanceQuestion" }, { step: intl.formatMessage({ id: OccurrenceStepLabelIdMap.SECOND_DIAGNOSIS }) })}
              </div>
            )}
            {hasError && (
              <>
                <div>
                  {intl.formatMessage({ id: "intl-msg:occurrence.step.errorChangeStepBody" })}
                </div>
                <div>{intl.formatMessage({ id: "intl-msg:occurrence.step.requiredFields" })}</div>
                <ul className="list-disc list-inside">
                  {props
                    .getNotFilledRequiredFields(
                      requiredServiceFields,
                      props.occurrence ?? ({} as Occurrence)
                    )
                    .map((field: string) => (
                      <li key={field}>
                        {formatFieldLabel(intl, field)}
                      </li>
                    ))}
                </ul>
              </>
            )}
          </div>
        }
        footer={
          <button
            className="btn btn-primary"
            disabled={hasError || !props.changeStep || !props.occurrence}
            onClick={async () => {
              if (!props.summaryStep?.id || !props.occurrence) return;
              try {
                await props.changeStep!(
                  props.occurrence!,
                  props.summaryStep.id,
                  OccurrenceStepEnum.SECOND_DIAGNOSIS
                );
                setShowSuccessDialog(false);
                props.onClose();
              } catch (error: any) {
                setShowSuccessDialog(false);
                setChangeStepErrorMessage(error?.message ?? intl.formatMessage({ id: "intl-msg:kanban.form.displacementForm.changeStepError" }));
              }
            }}
          >
            {intl.formatMessage({ id: "intl-msg:occurrence.step.advanceStepTitle" })}
          </button>
        }
      />
    </div>
  );
};

export default DisplacementForm;
